import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import type { TutoringAnalytics } from '@coffeeplexity/shared/src/types/aiTutoring';

interface TutoringOverviewCardProps {
  analytics: TutoringAnalytics | null;
  onStartSession: (
    topicId: string,
    sessionType: 'explanation' | 'practice' | 'quiz'
  ) => void;
  loading: boolean;
}

const TutoringOverviewCard: React.FC<TutoringOverviewCardProps> = ({
  analytics,
  onStartSession,
  loading,
}) => {
  const effectiveness = analytics?.overallEffectiveness;

  const quickActions = [
    {
      id: 'explanation',
      title: 'Get Explanation',
      subtitle: 'Adaptive learning content',
      icon: 'school',
      color: '#4CAF50',
      action: () => onStartSession('current-topic', 'explanation'),
    },
    {
      id: 'practice',
      title: 'Practice Session',
      subtitle: 'Skill-targeted exercises',
      icon: 'fitness-center',
      color: '#FF9800',
      action: () => onStartSession('practice-topic', 'practice'),
    },
    {
      id: 'quiz',
      title: 'Take Quiz',
      subtitle: 'AI-generated questions',
      icon: 'quiz',
      color: '#2196F3',
      action: () => onStartSession('quiz-topic', 'quiz'),
    },
  ];

  const renderMetricCard = (
    title: string,
    value: number | undefined,
    unit: string,
    icon: string,
    color: string
  ) => (
    <View style={[styles.metricCard, { borderLeftColor: color }]}>
      <View style={styles.metricHeader}>
        <Icon name={icon} size={20} color={color} />
        <Text style={styles.metricTitle}>{title}</Text>
      </View>
      <Text style={styles.metricValue}>
        {loading
          ? '...'
          : value !== undefined
            ? `${Math.round(value)}${unit}`
            : 'N/A'}
      </Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Learning Overview</Text>
        <Text style={styles.subtitle}>Your AI tutoring progress</Text>
      </View>

      {/* Effectiveness Metrics */}
      <View style={styles.metricsGrid}>
        {renderMetricCard(
          'Effectiveness',
          effectiveness?.effectivenessScore,
          '%',
          'trending-up',
          '#4CAF50'
        )}
        {renderMetricCard(
          'Learning Speed',
          effectiveness?.learningVelocity,
          '/hr',
          'speed',
          '#FF9800'
        )}
        {renderMetricCard(
          'Retention',
          effectiveness?.retentionRate,
          '%',
          'memory',
          '#2196F3'
        )}
        {renderMetricCard(
          'Engagement',
          effectiveness?.engagementLevel,
          '%',
          'favorite',
          '#E91E63'
        )}
      </View>

      {/* Quick Actions */}
      <View style={styles.actionsSection}>
        <Text style={styles.actionsTitle}>Quick Actions</Text>
        <View style={styles.actionsGrid}>
          {quickActions.map(action => (
            <TouchableOpacity
              key={action.id}
              style={styles.actionCard}
              onPress={action.action}
              activeOpacity={0.7}
            >
              <View
                style={[
                  styles.actionIcon,
                  { backgroundColor: `${action.color}15` },
                ]}
              >
                <Icon name={action.icon} size={24} color={action.color} />
              </View>
              <Text style={styles.actionTitle}>{action.title}</Text>
              <Text style={styles.actionSubtitle}>{action.subtitle}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Recent Insights */}
      {analytics?.overallEffectiveness && (
        <View style={styles.insightsSection}>
          <Text style={styles.insightsTitle}>AI Insights</Text>
          <View style={styles.insightCard}>
            <Icon name='lightbulb' size={20} color='#FF9800' />
            <View style={styles.insightContent}>
              <Text style={styles.insightText}>
                Your learning velocity has increased by{' '}
                {Math.round((effectiveness?.learningVelocity || 0) * 10)}% this
                week. Consider focusing on retention exercises to maintain
                progress.
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  metricTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    marginLeft: 6,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
  },
  actionsSection: {
    marginBottom: 24,
  },
  actionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  actionsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  actionCard: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  actionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  actionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    textAlign: 'center',
    marginBottom: 4,
  },
  actionSubtitle: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  insightsSection: {
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    paddingTop: 20,
  },
  insightsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  insightCard: {
    flexDirection: 'row',
    backgroundColor: '#fff8e1',
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#FF9800',
  },
  insightContent: {
    flex: 1,
    marginLeft: 12,
  },
  insightText: {
    fontSize: 14,
    color: '#1a1a1a',
    lineHeight: 20,
  },
});

export default TutoringOverviewCard;
